import React, { memo, useState, useEffect, useCallback } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import CustomButton from 'components/CustomButton';
import { capitalizeFirstLetterOfNWords } from 'utils';
import { CustomTooltip } from 'components/InfoTooltip';
import { getCaseMasterTopics } from 'services';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#fff',
    boxShadow: "0px 0px 4px 0px #00000026",
    borderRadius: '4px',
    padding: theme.spacing(7, 10),
  },
  header: {
    borderBottom: '1px solid #8692A3',
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '22px',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  content: {
    marginTop: theme.spacing(7),
  },
  cards: {
    display: 'flex', flexDirection: 'column', gap: '20px',
    flexWrap: 'wrap', marginTop: '8px'
  },
  cardRoot: {
    border: '2px solid',
    width: '100%',
    borderColor: theme.palette.neutral['clr-200'],
    overflow: 'hidden',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(4, 6),
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.palette.primary['clr-100'],
    }
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  skeleton: {
    borderRadius: '8px',
  },
  skeletonCardRoot: {
    width: '100%',
    border: '2px solid',
    borderColor: theme.palette.neutral['clr-200'],
    overflow: 'hidden',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(4, 6),
  },
  backIcon: {
    cursor: 'pointer',
    color: '#0062E1',
  },
}));

const TopicCard = memo(({
  name = '', totalQuestions = 0, completion = "0", onClick = () => { }
}) => {
  const classes = useStyles();

  return (
    <Box className={classes.cardRoot}>
      <Box className={classes.cardContent}>
        <Typography variant='h4-medium'>{capitalizeFirstLetterOfNWords(name)}</Typography>
        <Box display='flex' gap={1} mt={1}>
          <Typography variant='body01-bold' color='neutral.clr-900'>
            Total Cases: {totalQuestions} |&nbsp;
          </Typography>
          <Typography variant='body01-bold' color='neutral.clr-600'>
            Completed: {Math.round(completion)}%
          </Typography>
        </Box>
      </Box>
      <CustomButton
        variant='contained' color='primary'
        endIcon={<ArrowForwardIcon />}
        onClick={onClick}
      >
        View cases
      </CustomButton>
    </Box>
  );
});

const TopicCardSkeleton = () => {
  const classes = useStyles();

  return (
    <Box className={classes.skeletonCardRoot}>
      <Box className={classes.cardContent}>
        <Skeleton variant="text" width={180} height={32} className={classes.skeleton} />
        <Box display='flex' gap={1} mt={1}>
          <Skeleton variant="text" width={350} height={24} className={classes.skeleton} />
        </Box>
      </Box>
      <Skeleton variant="rectangular" width={160} height={40} className={classes.skeleton} />
    </Box>
  );
};

const Topics = ({ setSelected = () => { }, handleBack = () => { } }) => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [topicsData, setTopicsData] = useState([]);
  const [completionRate, setCompletionRate] = useState(0);

  // Topics are purely navigational: every "View cases" button is enabled so users
  // can browse all cases. Attempt access is gated per-case on the Challenges screen.
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Fetch topics from API
        const response = await getCaseMasterTopics();

        if (response && typeof response === 'object') {
          // Convert object to array and transform the data
          const topicsArray = Object.keys(response).map((key) => {
            const topicData = response[key];
            return {
              _id: topicData._id || topicData.tagId || key, // Use tag _id from API response
              name: topicData.topic || key,
              totalQuestions: topicData.totalQuestions || 0,
              completion: topicData.completion || 0,
              easy: topicData.easy || [0, 0],
              medium: topicData.medium || [0, 0],
              hard: topicData.hard || [0, 0],
              topicKey: key, // Keep the key for reference
            };
          });

          // Calculate overall completion rate
          let totalRate = 0;
          topicsArray.forEach((topic) => {
            totalRate += topic.completion;
          });

          setCompletionRate(topicsArray.length > 0 ? parseFloat(totalRate) / topicsArray.length : 0);
          setTopicsData(topicsArray);
        }
      } catch (error) {
        console.error('Error fetching CaseMaster topics:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleTopicClick = useCallback((data) => {
    setSelected(data);
  }, [setSelected]);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.title}>
          <ArrowBackIcon className={classes.backIcon} onClick={handleBack} />
          <Typography variant='h5-medium'>Case Study Topics</Typography>
        </Box>

        <CustomTooltip
          component='It represents your progress for case studies. It is based on your topics completion'
        >
          <Typography
            variant='h6-medium' color='primary.clr-300'
            style={{ borderBottom: '2px dotted #135EBF' }}
          >
            Completed: {completionRate.toFixed(0)}%
          </Typography>
        </CustomTooltip>
      </Box>

      <Box className={classes.content}>
        <Box className={classes.cards}>
          {loading ? (
            [...Array(3)].map((_, index) => (
              <TopicCardSkeleton key={`skeleton-${index}`} />
            ))
          ) : (
            topicsData.map((data) => (
              <TopicCard key={data._id} {...data} onClick={() => handleTopicClick(data)} />
            ))
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default Topics;
